import React, { useMemo, useState } from 'react';
import useOnChange from 'hooks/useOnChange';
import * as S from './styles';
import useDrawBlur from './useDrawBlur';

interface Dimensions {
  /**
   * Height of the canvas
   */
  height: number;
  /**
   * Width of the canvas
   */
  width: number;
}

export interface BlurredVideoBackgroundProps extends Dimensions {
  /**
   * video element to draw on the canvas
   */
  video: HTMLVideoElement | null;
}

/**
 * Component to draw a blurred video.  This is typically used when a design calls
 * for a video container in a particular aspect ratio but the video itself has a
 * different aspect ratio.
 *
 * The BlurredBackground takes the video, fills the frame, and blurs it. The background
 * animates along with the video.
 */
const BlurredVideoBackground: React.FC<BlurredVideoBackgroundProps> = ({
  height,
  video,
  width,
}) => {
  // dimensions need to be memoized for useOnChange
  const memoizedDimensions = useMemo(
    () => ({ height, width }),
    [height, width],
  );

  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const { draw } = useDrawBlur({ canvas, video });

  // canvas will clear itself if dimensions change, so redraw the background
  // if that happens
  useOnChange(memoizedDimensions, draw);

  return <S.Canvas {...{ height, width }} ref={setCanvas} />;
};

export default BlurredVideoBackground;
